import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import { updatePassword } from '../services/authService';
import './PasswordResetPage.css';

const PasswordResetPage: React.FC = () => {
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from location state (passed from LoginPage)
  const email = location.state?.email || '';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'password') {
      setPassword(value);
    } else if (name === 'confirmPassword') {
      setConfirmPassword(value);
    }
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validatePassword = (): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one digit';
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    const validationError = validatePassword();
    if (validationError) {
      setError(validationError);
      return;
    }

    if (!email) {
      setError('Email is required. Please go back to login page.');
      return;
    }

    setIsLoading(true);

    try {
      await updatePassword(email, password);
      // Success - redirect to login page
      navigate('/login', { state: { message: 'Password updated successfully. Please login with your new password.' } });
    } catch (err: any) {
      setError(err.message || 'Failed to update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="password-reset-page">
      <div className="password-reset-container">
        <div className="password-reset-box">
          <div className="password-reset-header">
            <Logo size="large" variant="purple" />
            <h2>Set Your Password</h2>
            <p>Please enter your new password</p>
          </div>
          
          <form onSubmit={handleSubmit} className="password-reset-form">
            <div className="form-group">
              <label htmlFor="password">New Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={password}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                placeholder="Enter your new password"
              />
              <div className="password-requirements">
                <p>Password must contain:</p>
                <ul>
                  <li className={password.length >= 8 ? 'met' : ''}>At least 8 characters</li>
                  <li className={/[A-Z]/.test(password) ? 'met' : ''}>One uppercase letter</li>
                  <li className={/[a-z]/.test(password) ? 'met' : ''}>One lowercase letter</li>
                  <li className={/[0-9]/.test(password) ? 'met' : ''}>One digit</li>
                  <li className={/[^A-Za-z0-9]/.test(password) ? 'met' : ''}>One special character</li>
                </ul>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={confirmPassword}
                onChange={handleInputChange}
                required
                disabled={isLoading}
                placeholder="Confirm your new password"
              />
            </div>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <button 
              type="submit" 
              className="password-reset-button"
              disabled={isLoading}
            >
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </button>
          </form>
          
          <div className="password-reset-footer">
            <button 
              type="button"
              className="back-to-login-link"
              onClick={() => navigate('/login')}
              disabled={isLoading}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
